import { useState } from "react";
import { Copy, Check, LucideIcon } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";

interface CopyButtonProps {
  text: string;
  variant?: "dark" | "light";
  size?: "sm" | "md" | "lg";
  className?: string;
  icon?: LucideIcon;
  tooltipText?: string;
}

export function CopyButton({
  text,
  variant = "dark",
  size = "md",
  className = "",
  icon: Icon = Copy,
  tooltipText = "Copy to clipboard",
}: CopyButtonProps) {
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  const sizeClasses = {
    sm: "px-2 py-1 text-xs",
    md: "px-3 py-2 text-xs",
    lg: "px-4 py-3 text-sm",
  };

  const variantClasses = {
    dark: "bg-gray-700 hover:bg-gray-600 text-gray-200 shadow-lg",
    light: "bg-white hover:bg-gray-100 text-gray-700 border border-gray-300",
  };

  const iconSizes = {
    sm: "w-3 h-3",
    md: "w-4 h-4",
    lg: "w-5 h-5",
  };

  return (
    <Tooltip delayDuration={700}>
      <TooltipTrigger asChild>
        <button
          onClick={handleCopy}
          className={cn(
            "flex items-center gap-2 font-medium rounded-md transition-colors duration-200 cursor-pointer",
            sizeClasses[size],
            variantClasses[variant],
            className
          )}
        >
          {copySuccess ? (
            <Check className={iconSizes[size]} />
          ) : (
            <Icon className={iconSizes[size]} />
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{tooltipText}</p>
      </TooltipContent>
    </Tooltip>
  );
}
